"use client"

import { useSyncExternalStore, type ReactNode } from "react"
import { localDayKey } from "@/lib/ssr/local-day-key"

const subscribeNever = () => () => {}

/**
 * Next's client error handler ignores errors carrying this digest, which is
 * the same one `next/dynamic` and `useSearchParams` use for a client-render
 * bailout.
 */
class HydrationBailout extends Error {
  readonly digest = "BAILOUT_TO_CLIENT_SIDE_RENDERING"
  constructor(reason: string) {
    super(`Bail out to client-side rendering: ${reason}`)
  }
}

/**
 * Pairs with `usePrerenderSafeSearchParams`. Static HTML is prerendered for
 * the no-query URL. When the page loads with a query string, hydrating that
 * HTML would mismatch (forms lazily initialise from the params), so the gate
 * throws during hydration only and React client-renders the nearest
 * `<Suspense>` instead. Without a query string, the server HTML hydrates as
 * is. Client-side navigations never hydrate, so they render straight through.
 *
 * `dateSensitive` subtrees derive visible copy from today's date. Their static
 * HTML is only hydratable on the calendar day it was rendered (the root layout
 * stamps that day on `<html data-rendered-on>`); on any other day they
 * client-render too.
 *
 * Must be placed directly inside a `<Suspense>` boundary.
 */
export function SearchParamsHydrationGate({
  children,
  dateSensitive = false,
}: {
  children: ReactNode
  dateSensitive?: boolean
}) {
  const isHydrating = useSyncExternalStore(subscribeNever, () => false, () => true)
  if (isHydrating && typeof window !== "undefined") {
    if (window.location.search.length > 1) {
      throw new HydrationBailout("query string present at hydration")
    }
    if (dateSensitive && document.documentElement.dataset.renderedOn !== localDayKey(new Date())) {
      throw new HydrationBailout("prerendered on a different day")
    }
  }
  return children
}
